// ── CallRegister 수정·삭제·킵 토글 액션 (useCallRegisterSubmit.js 에서 글자 그대로 옮김, 2026-10-13) ──
// - onEditSave: 수정 저장 (킵 자동해제 로직 포함)
// - onDelete: 콜 삭제
// - onToggleKeep: 킵 토글 (낙관적 업데이트 + 실패 시 롤백)
// - openEditModal: 수정 모달 열기 (킵 콜 방문일자 자동 설정 포함)

import { apiClient } from '../../../api/apiClient';
import { sanitizePhoneInput, makeEditForm } from './utils';

// callsUrl: 콜 쓰기 주소 — 기본 '/api/tm/calls', 통합 일일콜 메뉴는 메뉴 주소(2026-10-13)
export function createCallEditActions(core, {
  adminMode, showToast, fetchList, nextBizDate, callsUrl = '/api/tm/calls',
}, fetchLatestCall, openKeepReleaseModal) {
  const {
    salesPersonNames,
    editTarget, editForm,
    setEditSaving, setEditOpen,
    setDeletingId,
    setKeepingId, keepingId, setList,
  } = core;

  // ── 수정 저장 ─────────────────────────────────────────────────────────────
  async function onEditSave() {
    if (!editTarget?.id) return;

    // 원래 킵이었는데 수정 모달에서 해제한 경우 → 킵 해제 모달로 이동
    const keepWasReleased = Boolean(editTarget?.is_keep) && !Boolean(editForm?.is_keep);
    if (keepWasReleased) {
      setEditOpen(false);
      openKeepReleaseModal(editTarget);
      return;
    }

    const salesName = String(editForm.sales_person_name || '').trim();
    if (adminMode && !salesName) {
      showToast('마케팅담당자 이름을 입력해 주세요.', 'error');
      return;
    }
    if (adminMode && salesPersonNames.length > 0 && !salesPersonNames.includes(salesName)) {
      showToast('마케팅담당자 이름을 다시 확인해 주세요.', 'error');
      return;
    }

    setEditSaving(true);
    // 킵 콜에서 방문일자 설정 후 저장 시 킵 자동 해제
    const autoRemoveKeep = Boolean(editTarget?.is_keep) && Boolean(String(editForm.visit_date || '').trim());
    const r = await apiClient.put(`${callsUrl}/${editTarget.id}`, {
      ...editForm,
      phone: sanitizePhoneInput(editForm.phone),
      mobile_phone: sanitizePhoneInput(editForm.mobile_phone),
      sales_person_name: salesName,
      // ticket_type_id + ticket_type: 클라이언트가 결정한 값 그대로 전송
      ticket_type_id: editForm.ticket_type_id || null,
      ticket_type: editForm.ticket_type || null,
      ...((editTarget?.is_keep || editForm?.is_keep) ? { keep_memo: editForm.keep_memo || '' } : {}),
      ...(autoRemoveKeep ? { is_keep: false } : {}),
    });
    setEditSaving(false);

    if (!r.ok) { showToast('콜 정보 수정에 실패했습니다.', 'error'); return; }
    showToast('콜 정보를 수정했습니다.', 'success');
    setEditOpen(false);
    fetchList();
  }

  // ── 삭제 ─────────────────────────────────────────────────────────────────
  async function onDelete(row) {
    const callId = Number(row?.id);
    if (!Number.isInteger(callId) || callId <= 0) return;
    if (!window.confirm('이 콜을 삭제하시겠습니까?')) return;
    setDeletingId(callId);
    const result = await apiClient.del(`${callsUrl}/${callId}`);
    setDeletingId(null);
    if (!result.ok) { showToast(result.data?.message || '콜 삭제에 실패했습니다.', 'error'); return; }
    showToast('콜을 삭제했습니다.', 'success');
    fetchList();
  }

  // ── 킵 토글 (낙관적 업데이트) ─────────────────────────────────────────────
  async function onToggleKeep(row) {
    const callId = Number(row?.id);
    if (!Number.isInteger(callId) || callId <= 0 || keepingId === callId) return;
    const next = !row.is_keep;
    // 낙관적 업데이트: API 응답 전에 UI 먼저 반영
    setList((prev) => prev.map((r) => Number(r.id) === callId ? { ...r, is_keep: next } : r));
    setKeepingId(callId);
    const endpoint = adminMode
      ? `/api/admin/tm/calls/${callId}/keep`
      : `/api/tm/calls/${callId}/keep`;
    const res = await apiClient.patch(endpoint, { is_keep: next, company_name: row.company_name || null });
    setKeepingId(null);
    if (!res.ok) {
      // 실패 시 롤백
      setList((prev) => prev.map((r) => Number(r.id) === callId ? { ...r, is_keep: !next } : r));
      showToast('킵 처리에 실패했습니다.', 'error');
    } else {
      showToast(next ? '킵목록에 추가되었습니다.' : '킵목록에서 제거되었습니다.', 'success');
    }
  }

  // ── 수정 모달 열기 (킵 콜 방문일자 자동 설정 포함) ────────────────────────
  async function openEditModal(row) {
    if (row.is_contracted) return;
    const latest = await fetchLatestCall(row);
    if (!latest || latest.is_contracted) return;
    core.setEditTarget(latest);
    const ef = makeEditForm(latest);
    // 킵 콜: 방문일자 미입력 시 다음 영업일 자동 설정
    if (latest.is_keep && !ef.visit_date) {
      ef.visit_date = await nextBizDate();
    }
    core.setEditForm(ef);
    core.setEditOpen(true);
  }

  return { onEditSave, onDelete, onToggleKeep, openEditModal };
}
